import React, { useState } from "react";
import { Card, Typography, CardContent, Button, Avatar } from "@material-ui/core";
import Markdown from "react-markdown";
import moment from "moment";
import { FeedItem } from "api/feed";
import { Booking, BookingType, patchCheckIn, patchInvitation } from "api/bookings";
import { useSnackbar } from "notistack";
import styles from "features/calendar/CalendarView/CalendarView.module.scss";
import { formatter, markdownIfYoutubelink } from "../../../utils/helpers";
import i18n from "./i18n";
import "./homeView.scss";

// prop types
interface BookingFeedCardProps {
    feedItem: FeedItem,
}

// booking styling (see BookingCell.tsx)
const getStyle = ({ bookingStatus, bookingType }: Booking) => {
    if (bookingType === BookingType.Meetup) {
        return { border: `2px solid ${styles.bookingStandardFinalColor}` };
    }
    if (bookingStatus === "pending") {
        return { background: styles.bookingPendingColor };
    }
    if (bookingType === BookingType.Standard) {
        return { background: styles.bookingStandardColor };
    }
    if (bookingType === BookingType.Training) {
        return { background: styles.bookingTrainingColor };
    }
    return {};
};

// format date
const formatDate = (date: string) => {
    const m = moment(date);
    if (m.isSame(moment(), "day")) return m.format(`[${i18n.today}] \u2022 H:mm`);
    if (m.isSame(moment().subtract(1, "day"), "day")) return m.format(`[${i18n.yesterday}] \u2022 H:mm`);
    return m.format("DD.MM.YYYY \u2022 H:mm");
};

// format participants
const formatParticipants = (booking: Booking) => {
    const joined = booking.participants.map((p) => `${p.user.firstName} ${p.user.lastName}`).join(", ");
    const hidden = booking.numTotalParticipants - booking.participants.length;
    if (hidden === 0) return joined;
    return `${joined} ${i18n.andMore(hidden)}`;
};

// booking feed item card
const BookingFeedCard = ({ feedItem }: BookingFeedCardProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    // ui state
    const [isLoading, setLoading] = useState(false);
    const expectedPriceCents = feedItem.paymentInfo?.priceCents || 0;

    // answer booking invitation
    const answerBookingInvitation = async (accept: boolean) => {
        setLoading(true);
        try {
            await patchInvitation({
                accept,
                bookingId: feedItem.booking!.id,
                clientLatitude: feedItem.club!.latitude,
                clientLongitude: feedItem.club!.longitude,
                expectedPriceCents,
            });
            enqueueSnackbar(i18n.answerInvitationSuccess(accept), { variant: "success" });
        } catch (error) {
            displayError(error);
        }
        setLoading(false);
    };

    // perform check-in
    const performCheckIn = async () => {
        setLoading(true);
        try {
            await patchCheckIn({
                bookingId: feedItem.booking!.id,
                clientLatitude: feedItem.club!.latitude,
                clientLongitude: feedItem.club!.longitude,
            });
            enqueueSnackbar(i18n.checkInSuccess, { variant: "success" });
        } catch (error) {
            displayError(error);
        }
        setLoading(false);
    };

    // render
    return (
        <Card style={getStyle(feedItem.booking!)}>
            <CardContent style={{ padding: "16px" }}>
                {/* info */}
                <Typography variant="subtitle2">{feedItem.booking!.description || i18n.bookingTypes[feedItem.booking!.bookingType]}</Typography>
                <Typography variant="caption" component="p" className="marginBottom">
                    {`${formatDate(feedItem.booking!.startDate)} - ${moment(feedItem.booking!.endDate).format("H:mm")}`}
                    {" \u2022 "}
                    {feedItem.booking!.court.courtName}
                </Typography>
                {feedItem.booking?.detailsDescription && (
                    <>
                        <Markdown linkTarget="_blank">{feedItem.booking!.detailsDescription}</Markdown>
                    </>
                )}
                {feedItem.booking?.privateDescription && (
                    <>
                        <Typography variant="body2" style={{ fontWeight: "bold" }}>{i18n.moreInformation}</Typography>
                        <Markdown linkTarget="_blank">{markdownIfYoutubelink(feedItem.booking!.privateDescription)}</Markdown>
                    </>
                )}
                {feedItem.booking?.galleryMediaUrls && (feedItem.booking?.galleryMediaUrls).length > 0 && (
                    <Avatar src={feedItem.booking?.galleryMediaUrls[0]} className="club_avatar marginBottom" />
                )}
                <Typography variant="caption" component="p">
                    {formatParticipants(feedItem.booking!)}
                </Typography>

                {/* actions */}
                {feedItem.bookingOptionsInfo!.possibleOptions.includes("answer-invitation") && (
                    <>
                        <Button variant="contained" style={{ marginTop: "8px", marginRight: "8px" }} onClick={() => answerBookingInvitation(true)} disabled={isLoading}>
                            {i18n.accept(expectedPriceCents ? formatter.format(expectedPriceCents / 100) : "")}
                        </Button>
                        <Button variant="outlined" style={{ marginTop: "8px", marginRight: "8px" }} onClick={() => answerBookingInvitation(false)} disabled={isLoading}>
                            {i18n.decline}
                        </Button>
                    </>
                )}
                {feedItem.bookingOptionsInfo!.possibleOptions.includes("check-in") && (
                    <Button variant="contained" style={{ marginTop: "8px", marginRight: "8px" }} onClick={performCheckIn} disabled={isLoading}>
                        {i18n.checkIn}
                    </Button>
                )}
            </CardContent>
        </Card>
    );
};

export default BookingFeedCard;
